import { useCallback, useEffect, useState } from "react";

import {
  CheckGreen,
  ErrorTriangle,
} from "@hl/base-components/lib/assets/icons.generated";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { logError } from "@hl/shared-features/lib/services/logger";
import { Loader, Stack, Text, TextInput, useMantineTheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { isEmpty } from "lodash";

import {
  isSeriesCollection,
  isSeriesLikeCollection,
} from "~/features/collections/displayUtils";
import { GetManageCollectionQuery } from "~/features/manage-collection/v2.queries.graphql.generated";
import { useGetCollectionTokensV2LazyQuery } from "~/features/tokens/sections/ManageToken/properties/FeaturedToken.graphql.generated";
import { useUpdateCollectionOffChainMutation } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

export const FeaturedToken = ({
  collection,
  refetch,
}: {
  collection?: GetManageCollectionQuery["getManageCollection"];
  refetch?: () => void;
}) => {
  const theme = useMantineTheme();
  const colType = collection?.collectionType;
  const isAnySeries = isSeriesLikeCollection(colType);
  const currentFeaturedTokenId = isSeriesCollection(colType)
    ? collection?.seriesDetails?.featuredTokenId
    : collection?.generativeDetails?.featuredTokenId;
  const [error, setError] = useState<string>("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [getCollectionTokensV2, { loading: loadingTokens }] =
    useGetCollectionTokensV2LazyQuery();
  const form = useForm<{ featuredTokenId?: string }>({
    initialValues: {
      featuredTokenId: currentFeaturedTokenId || "",
    },
    validate: (values) => ({
      featuredTokenId: isEmpty(values.featuredTokenId)
        ? null
        : values.featuredTokenId &&
          (values.featuredTokenId.length < 1 ||
            values.featuredTokenId.length > 9)
        ? "Featured token ID must have between 1 and 9 chars"
        : null,
    }),
  });
  const [debouncedFeaturedTokenId] = useDebouncedValue(
    form.values.featuredTokenId,
    200
  );
  const [updateCollectionOffChain, { loading: loadingOffChainUpdate }] =
    useUpdateCollectionOffChainMutation();

  const editFeaturedTokenId = useCallback(
    async ({ featuredTokenId }: { featuredTokenId?: string }) => {
      if (
        (!featuredTokenId && !currentFeaturedTokenId) ||
        featuredTokenId === currentFeaturedTokenId
      )
        return;
      if (!collection) return;
      setIsUpdated(false);
      setError("");

      try {
        if (isAnySeries) {
          if (featuredTokenId) {
            const tokensResponse = await getCollectionTokensV2({
              variables: {
                tokenNameOrId: featuredTokenId,
                tokenAddress: collection.address,
                chainId: collection.chainId,
              },
            });
            if (!tokensResponse?.data?.getCollectionTokensV2.edges?.[0]) {
              setError(
                "Token with this Token ID does not exist. Please enter a different one."
              );
              return;
            }
          }
          await updateCollectionOffChain({
            variables: {
              collectionId: collection.id,
              data: {
                featuredTokenId: featuredTokenId || null,
              },
            },
          });
          setIsUpdated(true);
          refetch?.();
        }
      } catch (e) {
        setError("Failed to save changes. Please try again.");
        await logError({
          e,
          message: "[EditFeaturedTokenId] Failed to update",
        });
      }
    },
    [collection]
  );

  useEffect(() => {
    form.validate();
  }, [form.values]);

  useEffect(() => {
    editFeaturedTokenId({ featuredTokenId: debouncedFeaturedTokenId });
  }, [debouncedFeaturedTokenId]);

  const loading = loadingTokens || loadingOffChainUpdate;

  return isAnySeries ? (
    <Stack w="100%" spacing={8}>
      <TextInput
        placeholder="Token ID"
        hideControls
        label="Featured token"
        rightSection={
          error ? (
            <ErrorTriangle color={theme.colors.errorStatus[0]} />
          ) : loading ? (
            <Loader width={16} />
          ) : isUpdated ? (
            <CheckGreen />
          ) : null
        }
        description="Choose an already minted token to feature first when someone visits your mint page. If you choose not to select one, we'll pick one for you at random."
        {...form.getInputProps("featuredTokenId")}
      />
      {error && (
        <Text size="xs" color={STATUS_COLOR.ERROR}>
          {error}
        </Text>
      )}
      {!error && !isUpdated && !loading && (
        <Text size="xs" color={TEXT_COLOR.SECONDARY}>
          Find the token ID by clicking your desired token in the marketplace;
          it&apos;s under &apos;Details&apos;
        </Text>
      )}
    </Stack>
  ) : null;
};
